import { store } from '@/store';
import imgSteps from '@/assets/img/news/77-inside-1.webp';
import imgGallery from '@/assets/img/news/77-inside-2.webp';
import imgCase1 from '@/assets/img/news/77-inside-3.webp';
import imgCase2 from '@/assets/img/news/77-inside-4.webp';
import imgWorkflow from '@/assets/img/news/77-inside-5.webp';
import imgBenefits from '@/assets/img/news/77-inside-6.webp';

const openAskForm = () => store.askFormStore.openModal('news_3d_agencies');

export default {
  date: 'June 22, 2026',
  title: 'Why <span style="color: #FFA900">3D Digital Agencies</span> Hire <span style="color: #FFA900">Getfloorplan</span>',
  description: 'Getfloorplan helps 3D digital agencies create 3D tours, renders, 2D and 3D floor plans for developer projects faster — with AI production, human review and custom interior style integration.',
  metaTitle: 'How 3D Agencies Scale Tours, Renders and Floor Plans with Getfloorplan',
  metaDescription: 'Learn how 3D digital agencies use Getfloorplan to create 3D tours, renders and floor plans faster, reduce manual work and scale production for developer projects.',
  id: 'why-3d-digital-agencies-hire-getfloorplan',
  thumb: 'news/77.webp',
  jsx: () => (
    <div class="novelty__text">
      <p>Getfloorplan helps 3D digital agencies produce 2D floor plans, 3D floor plans, 360 virtual tours and interior renders <span style="color: #FFA900">5× faster</span> and <span style="color: #FFA900">10× cheaper</span> — with lower manual production costs.</p>

      <p style="margin-top: 30px; font-size: 24px; line-height: 1.3">
        <b>Manual <span style="color: #FFA900">3D property</span> model production becomes a bottleneck</b>
      </p>
      <p>For every new developer project, your agency needs more production work:</p>
      <div class="novelty__thumb" onClick={openAskForm} style="cursor: pointer">
        <img src={imgSteps} alt="Manual 3D production steps — budget and time" />
      </div>
      <p>Manually creating the 3D exterior of a building is fine for a 3D studio, but creating 3D tours for 50–100 apartments isn’t.</p>
      <p>One 3D tour can take 1–3 days of manual work.</p>
      <p>When all production depends on one or two 3D designers, every new project increases pressure on your team.</p>
      <ul style="margin-left: 32px">
        <li class="margin-none">More layouts.</li>
        <li class="margin-none">More deadlines.</li>
        <li class="margin-none">More manual hours.</li>
        <li class="margin-none">Higher production cost.</li>
      </ul>
      <p>That is why production becomes the bottleneck for growing 3D agencies.</p>

      <p style="margin-top: 30px; font-size: 24px; line-height: 1.3">
        <b>How 3D studios use Getfloorplan in full 3D property projects?</b>
      </p>
      <p style="margin-top: 10px; font-size: 24px; line-height: 1.3">
        <b>What AI Getfloorplan creates</b>
      </p>
      <div class="novelty__thumb">
        <img src={imgGallery} alt="2D floor plan, 3D floor plan, 360 tour, renders and full colored floor" />
      </div>
      <p>So your agency can deliver developer projects faster without expanding the team.</p>
      <ul style="margin-left: 32px">
        <li class="margin-none">AI speeds up production.</li>
        <li class="margin-none">Human review keeps the result under control.</li>
        <li class="margin-none">Custom interiors and exact window views — all of it can be done.</li>
        <li class="margin-none">White Label included.</li>
      </ul>

      <p style="margin-top: 30px; font-size: 24px; line-height: 1.3">
        <b>Case 1. How <span style="color: #FFA900">59</span> 3D tours were converted from 2D in <span style="color: #FFA900">1 week</span> instead of 2 months?</b>
      </p>
      <p>Input: the exterior building and surroundings created by the 3D studio. The apartments had to exist as 3D tours — each apartment in 4K resolution, with a warm, cozy interior tour.</p>
      <p>Process:</p>
      <ol style="margin-left: 32px">
        <li class="margin-none">The client chose the interior from the regular Getfloorplan styles — 1 day.</li>
        <li class="margin-none">1 day to create the demo.</li>
        <li class="margin-none">The client asked to change the window frames and add more flowers.</li>
        <li class="margin-none">3 days — demo updated, client approved.</li>
        <li class="margin-none">59 unit tours processed.</li>
        <li class="margin-none">The client checked and approved the tours; the 3D studio added the tours’ links into the full 3D model.</li>
        <li class="margin-none">This helps agencies take larger developer projects without immediately hiring more 3D designers.</li>
      </ol>
      <div class="novelty__thumb">
        <img src={imgCase1} alt="Manual vs Getfloorplan: 59 apartments in 1.5–2 months vs about 1 week" />
      </div>

      <p style="margin-top: 30px; font-size: 24px; line-height: 1.3">
        <b>Case 2. 117 apartments with a real view from every window</b>
      </p>
      <p>Input:</p>
      <ul style="margin-left: 32px">
        <li class="margin-none">DWG files for 117 apartments.</li>
        <li class="margin-none">Renders of the existing style.</li>
        <li class="margin-none">Panoramas for each apartment — the client wanted a real view from the window for every apartment.</li>
      </ul>
      <div class="novelty__thumb">
        <img src={imgCase2} alt="117 apartment tours — who will create them?" />
      </div>
      <p>Processing took 5 days for 117 units with a real view from the window.</p>
      <p>Roughly 1 month for the full building of 117 unique apartments — each with its real view, plus the client’s interior style.</p>

      <p style="margin-top: 30px; font-size: 24px; line-height: 1.3">
        <b>How the workflow works</b>
      </p>
      <div class="novelty__thumb">
        <img src={imgWorkflow} alt="Workflow: input, interior style setup, demo tour in 24 hours, production, human review" />
      </div>

      <p style="margin-top: 30px; font-size: 24px; line-height: 1.3">
        <b>How agencies cut costs on 3D designers and production</b>
      </p>
      <p>Manual 3D production is expensive because every project depends on designer hours, management time and revision cycles.</p>
      <p>Getfloorplan helps agencies reduce the internal production load.</p>
      <div class="novelty__thumb">
        <img src={imgBenefits} alt="Less manual work, lower production cost, higher speed, less pressure on 3D designers" />
      </div>
      <p>It is about helping your team deliver more developer projects with less manual production pressure.</p>

      <p style="margin-top: 30px; font-size: 24px; line-height: 1.3">
        <b>AI speed with human review</b>
      </p>
      <p>Getfloorplan combines AI production with human review.</p>
      <p>This means your agency gets speed, but the result is still checked against the project requirements.</p>
      <p>We review:</p>
      <ul style="margin-left: 32px">
        <li class="margin-none">visual consistency;</li>
        <li class="margin-none">output readiness before delivery;</li>
        <li class="margin-none">floor plan logic;</li>
        <li class="margin-none">room structure;</li>
        <li class="margin-none">approved interior style;</li>
      </ul>
      <p>If the project uses the client’s interior style, we first collect references, approve the style direction and then apply it across production.</p>
      <p>This helps agencies keep quality under control while reducing manual work.</p>

      <p style="margin-top: 30px; font-size: 24px; line-height: 1.3">
        <b>Ready to scale your 3D production?</b>
      </p>
      <p>Use Getfloorplan as your AI production partner for developer projects.</p>
      <p>Send us a floor plan and see how fast your agency can produce 3D floor plans, 360 tours, renders and marketing visuals.</p>
      <div style="text-align: center; margin-top: 16px">
        <button
          type="button"
          class="novelty__cta"
          onClick={openAskForm}
          style="display: inline-flex; align-items: center; justify-content: center; padding: 16px 36px; background: #FFA900; color: #413E3E; font-size: 18px; font-weight: 700; line-height: 1; border: none; border-radius: 10px; cursor: pointer">
          Scale your 3D production
        </button>
      </div>

      <p style="margin-top: 30px; font-size: 24px; line-height: 1.3">
        <b>Short FAQ</b>
      </p>

      <p style="margin-top: 24px; font-size: 20px"><b>Do we need to replace our 3D team?</b></p>
      <p>No. Getfloorplan helps your team reduce manual production work, not replace your agency.</p>
      <p>Your team keeps client communication, creative direction and final approval.</p>

      <p style="margin-top: 24px; font-size: 20px"><b>Can we use Getfloorplan for developer projects?</b></p>
      <p>Yes. Agencies use Getfloorplan for developer websites, listings, ads, brochures, sales presentations and pre-sales campaigns.</p>

      <p style="margin-top: 24px; font-size: 20px"><b>What do we need to start?</b></p>
      <p>You can start with floor plans and references.</p>
      <p>If you want to use your client’s interior style, send style references as well.</p>

      <p style="margin-top: 24px; font-size: 20px"><b>Can Getfloorplan help with many layouts?</b></p>
      <p>Yes. Getfloorplan is useful when your agency needs to process multiple units, apartments or layouts faster than manual production allows.</p>

      <p style="margin-top: 24px; font-size: 20px"><b>Is human review included?</b></p>
      <p>Yes. Human review is included to check the result against the brief, floor plan and approved style.</p>

      <p style="margin-top: 24px; font-size: 20px"><b>Can you work with the client’s interior style?</b></p>
      <p>Yes. We can work with Getfloorplan styles or integrate your client’s interior style after collecting and approving references.</p>
    </div>
  ),
};
